/**
 * Team Season Analytics — V1.0
 *
 * Bound to:
 *   GET /api/team-season-analytics/team/{team_id}?start_date=&end_date=
 *
 * Sections (in order):
 *   1  Hero KPI strip (4 cards — Availability · Total Load · Wellness · Injury Burden)
 *   2  Season Narrative
 *   3  Squad Availability Timeline + Weekly Availability Trend
 *   4  Training Load (squad weekly bars)
 *   5  Wellness (weekly mean + red-flag count)
 *   6  Performance Progress (Jump · VBT · Aerobic) with Top-3 leaderboards
 *   7  Training Distribution (donut + table)
 *   8  Injury Analytics (type · region · severity · monthly trend · recurrences)
 *   9  Rankings & Outliers (Top/Bottom 5 + 4 flag lists)
 *  10  Comparisons (3 modes — prev season · other teams · category)
 *  11  Key Findings & Recommendations
 */
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  ArrowLeft, Activity, Award, AlertTriangle, Calendar, ChevronRight,
  Download, FileSpreadsheet, Heart, Loader2, ShieldAlert,
  Trophy, Users, Zap,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import TeamMatchExposureSection from '../components/reports/TeamMatchExposureSection';
import { LongitudinalProgress } from '../components/reports/LongitudinalProgress';
import { LongitudinalSummary } from '../components/reports/LongitudinalTables';
import { useSeasonReport } from '../hooks/useSeasonReport';
import { TeamCalendarSummary } from '../components/reports/TeamCalendarSummary';

const PALETTE = ['#1E6BFF', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

const fmt = (v, suffix = '') => (v === null || v === undefined ? '—' : `${v}${suffix}`);
const fmtPct = (v) => (v === null || v === undefined ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`);

const KpiCard = ({ label, value, hint, accent = 'primary', testid }) => (
  <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">
      {label}
    </div>
    <div className={`text-3xl font-bold ${
      accent === 'good' ? 'text-emerald-400'
        : accent === 'warn' ? 'text-amber-400'
        : accent === 'critical' ? 'text-red-400'
        : 'text-cyan-300'
    }`}>
      {value}
    </div>
    {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
  </Card>
);

const SectionTitle = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-3 mb-3">
    {Icon && <Icon className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />}
    <div>
      <h2 className="text-lg font-semibold text-zinc-100">{title}</h2>
      {subtitle && <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

const NoData = ({ msg }) => (
  <div className="text-xs text-zinc-500 italic py-4">{msg || 'No data available.'}</div>
);

const tooltipStyle = {
  backgroundColor: '#0f172a',
  border: '1px solid #334155',
  borderRadius: '6px',
  fontSize: '11px',
};

// ─── Section components ──────────────────────────────────────────
const SquadAvailability = ({ weeks }) => {
  if (!weeks?.length) return <NoData />;
  const data = weeks.map(w => ({
    week: w.week_start.slice(5),
    Available: w.available,
    RTP: w.rtp,
    Lost: w.lost,
  }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
        <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
        <XAxis dataKey="week" tick={{ fill: '#94a3b8', fontSize: 9 }} interval="preserveStartEnd" />
        <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
        <Tooltip contentStyle={tooltipStyle} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="Available" stackId="a" fill="#22C55E" />
        <Bar dataKey="RTP" stackId="a" fill="#F59E0B" />
        <Bar dataKey="Lost" stackId="a" fill="#EF4444" />
      </BarChart>
    </ResponsiveContainer>
  );
};

const AvailabilityTrend = ({ trend }) => {
  if (!trend?.length) return <NoData />;
  const data = trend.map(t => ({
    week: t.week_start.slice(5),
    pct: t.availability_pct,
  }));
  return (
    <ResponsiveContainer width="100%" height={140}>
      <LineChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
        <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
        <XAxis dataKey="week" tick={{ fill: '#94a3b8', fontSize: 9 }} interval="preserveStartEnd" />
        <YAxis domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 10 }} />
        <Tooltip contentStyle={tooltipStyle} />
        <Line type="monotone" dataKey="pct" stroke="#06B6D4" dot={false} strokeWidth={2} />
      </LineChart>
    </ResponsiveContainer>
  );
};

const WeeklyLoadChart = ({ weekly }) => {
  if (!weekly?.length) return <NoData />;
  const data = weekly.map(w => ({ week: w.week.slice(5), load: w.load }));
  return (
    <ResponsiveContainer width="100%" height={180}>
      <BarChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
        <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
        <XAxis dataKey="week" tick={{ fill: '#94a3b8', fontSize: 9 }} interval="preserveStartEnd" />
        <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
        <Tooltip contentStyle={tooltipStyle} />
        <Bar dataKey="load" fill="#1E6BFF" />
      </BarChart>
    </ResponsiveContainer>
  );
};

const WellnessChart = ({ weekly }) => {
  if (!weekly?.length) return <NoData />;
  const data = weekly.map(w => ({ week: w.week.slice(5), avg: w.avg, red: w.red_flags }));
  return (
    <ResponsiveContainer width="100%" height={180}>
      <LineChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
        <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
        <XAxis dataKey="week" tick={{ fill: '#94a3b8', fontSize: 9 }} interval="preserveStartEnd" />
        <YAxis domain={[0, 10]} tick={{ fill: '#94a3b8', fontSize: 10 }} />
        <Tooltip contentStyle={tooltipStyle} />
        <Legend wrapperStyle={{ fontSize: 10 }} />
        <Line type="monotone" dataKey="avg" stroke="#22C55E" dot={false} strokeWidth={2} name="Avg score" />
        <Line type="monotone" dataKey="red" stroke="#EF4444" dot={false} strokeWidth={2} name="Red flags" />
      </LineChart>
    </ResponsiveContainer>
  );
};

const DistributionDonut = ({ categories }) => {
  if (!categories?.length) return <NoData />;
  const data = categories.filter(c => c.minutes > 0).map(c => ({ name: c.category, value: c.minutes }));
  if (!data.length) return <NoData />;
  return (
    <ResponsiveContainer width="100%" height={210}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
          {data.map((d, i) => (
            <Cell key={d.name} fill={d.name.toUpperCase() === 'RTP' ? '#EF4444' : PALETTE[i % PALETTE.length]} />
          ))}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} />
        <Legend wrapperStyle={{ fontSize: 10 }} />
      </PieChart>
    </ResponsiveContainer>
  );
};

const MonthlyInjuryTrend = ({ monthly }) => {
  if (!monthly?.length) return <NoData msg="No injuries this season." />;
  const data = monthly.map(m => ({ month: m.month, count: m.count }));
  return (
    <ResponsiveContainer width="100%" height={170}>
      <BarChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
        <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
        <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 10 }} />
        <YAxis allowDecimals={false} tick={{ fill: '#94a3b8', fontSize: 10 }} />
        <Tooltip contentStyle={tooltipStyle} />
        <Bar dataKey="count" fill="#EF4444" />
      </BarChart>
    </ResponsiveContainer>
  );
};

const RankList = ({ title, rows, suffix = '', testid }) => (
  <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid={testid}>
    <div className="text-xs uppercase text-zinc-500 mb-3">{title}</div>
    {rows?.length ? (
      <div className="space-y-1.5">
        {rows.map((r, i) => (
          <div key={r.athlete_id} className="flex justify-between text-xs py-1 border-b border-zinc-800/60 last:border-0">
            <span className="text-zinc-300">{i + 1}. {r.name}</span>
            <span className="text-zinc-100 font-semibold tabular-nums">{r.value}{suffix}</span>
          </div>
        ))}
      </div>
    ) : <NoData />}
  </Card>
);

const FlagList = ({ title, rows, render, icon: Icon, accent = 'amber', testid }) => (
  <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <div className="text-xs uppercase text-zinc-500 flex items-center gap-2">
        {Icon && <Icon className={`w-4 h-4 ${accent === 'red' ? 'text-red-400' : 'text-amber-400'}`} />}
        {title}
      </div>
      <Badge variant="outline" className={`text-[10px] ${
        rows?.length
          ? (accent === 'red' ? 'border-red-500/40 text-red-300' : 'border-amber-500/40 text-amber-300')
          : 'border-zinc-700 text-zinc-500'
      }`}>
        {rows?.length || 0}
      </Badge>
    </div>
    {rows?.length ? (
      <div className="space-y-1.5">
        {rows.slice(0, 8).map((r, i) => (
          <div key={i} className="text-xs flex justify-between py-1 border-b border-zinc-800/60 last:border-0">
            {render(r)}
          </div>
        ))}
      </div>
    ) : <div className="text-xs text-zinc-500 italic py-2">No flags raised.</div>}
  </Card>
);

const ComparisonCard = ({ title, mode, testid }) => {
  if (!mode) return null;
  const small = mode.validity === 'SMALL_SAMPLE';
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid={testid}>
      <div className="comparison-heading flex items-center justify-between mb-3">
        <div className="text-xs uppercase text-zinc-500">{title}</div>
        {small && (
          <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-300">
            Small Sample
          </Badge>
        )}
      </div>
      <div className="space-y-1.5">
        {mode.rows.map((r, i) => (
          <div key={i} className="comparison-metric-row flex items-center justify-between text-xs py-1.5 border-b border-zinc-800/60 last:border-0">
            <span className="text-zinc-400">{r.label}</span>
            <div className="flex items-center gap-3">
              <span className="text-zinc-200 tabular-nums">{fmt(r.current)}</span>
              <span className="text-zinc-500 tabular-nums">vs {fmt(r.baseline)}</span>
              <span className="tabular-nums font-semibold w-14 text-right text-zinc-300"
                data-testid={`${testid}-gap-${i}`}>{fmtPct(r.gap_pct)}</span>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ─── Main page ──────────────────────────────────────────────────
const TeamSeasonAnalyticsPage = () => {
  const { api } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { teamId } = useParams();
  const t = (en, it) => (language === 'IT' ? it : en);

  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const { data, loading, error } = useSeasonReport({ api,
    path: teamId ? `/team-season-analytics/team/${teamId}` : null,
    start, end, setStart, setEnd, language });
  const [exporting, setExporting] = useState(null); // 'pdf' | 'xlsx' | 'batch' | null

  const downloadBlob = async (path, body, filename, label) => {
    setExporting(label);
    try {
      const opts = body
        ? [path, body, { responseType: 'blob' }]
        : [path, { responseType: 'blob' }];
      const res = body
        ? await api.post(...opts)
        : await api.get(...opts);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${label.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to export ${label}`);
    } finally {
      setExporting(null);
    }
  };

  const handleExportPdf = () => downloadBlob(
    '/premium-reports/team-season',
    { team_id: teamId, start_date: start, end_date: end, language },
    `team_season_report_${teamId.slice(0, 8)}.pdf`, 'pdf');

  const handleExportXlsx = () => downloadBlob(
    `/team-season-analytics/team/${teamId}/excel` +
      `?language=${language}` + (start && end ? `&start_date=${start}&end_date=${end}` : ''),
    null,
    `team_season_${teamId.slice(0, 8)}.xlsx`, 'xlsx');

  const handleExportBatch = () => downloadBlob(
    '/premium-reports/team-season-batch',
    { team_id: teamId, start_date: start, end_date: end },
    `team_${teamId.slice(0, 8)}_SeasonReports.zip`, 'batch');

  const teamLabel = useMemo(() => {
    if (!data?.team) return '';
    return data.team.name || data.team.code || '';
  }, [data]);

  if (!teamId) return <div className="p-6 text-zinc-300">No team selected.</div>;
  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-24 text-zinc-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('Loading…', 'Caricamento…')}
      </div>
    );
  }
  if (error && !data) return <div className="p-6 text-red-400" data-testid="team-season-load-error">{error}</div>;
  if (!data) return null;

  const {
    kpis, availability, availability_trend: trend, load, wellness,
    performance, training_distribution: td, injuries, rankings, outliers,
    comparisons, narrative, findings, recommendations,
  } = data;

  return (
    <div className="season-report space-y-6 fade-in" data-testid="team-season-analytics-page" aria-busy={loading}>
      {error && <p role="alert" className="text-red-300" data-testid="team-season-filter-error">{error}</p>}
      {loading && <p role="status" className="text-zinc-400" data-testid="team-season-updating">{t('Updating…', 'Aggiornamento…')}</p>}
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Button variant="ghost" size="sm" className="text-zinc-400 -ml-2 mb-1"
            onClick={() => navigate('/staff/roster')}
            data-testid="team-season-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> {t('Back to roster', 'Indietro')}
          </Button>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100">
            {t('Team Season Analytics', 'Analytics di Stagione Squadra')}
            {teamLabel && <span className="text-zinc-400 font-normal"> — {teamLabel}</span>}
          </h1>
          <p className="text-sm text-zinc-500 mt-1" data-testid="team-season-visible-period">
            {data.period.start_date} → {data.period.end_date}
            <span className="mx-2">·</span>
            {data.squad_size} {t('athletes', 'atleti')}
            {data.team?.category && <><span className="mx-2">·</span>{data.team.category}</>}
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-[10px] uppercase text-zinc-500 block">{t('Start', 'Inizio')}</label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)}
              className="bg-zinc-900 border-zinc-700 text-zinc-100 h-9 w-40"
              data-testid="team-season-start-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-zinc-500 block">{t('End', 'Fine')}</label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
              className="bg-zinc-900 border-zinc-700 text-zinc-100 h-9 w-40"
              data-testid="team-season-end-input" />
          </div>
          <Button onClick={handleExportPdf} disabled={!!exporting || loading || !!error}
            className="h-9 bg-cyan-600 hover:bg-cyan-500 text-white"
            data-testid="export-team-season-pdf-btn">
            {exporting === 'pdf'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Download className="w-4 h-4 mr-2" />}
            {t('Team PDF', 'PDF Squadra')}
          </Button>
          <Button onClick={handleExportXlsx} disabled={!!exporting || loading || !!error}
            variant="outline"
            className="h-9 border-zinc-700 text-zinc-200 hover:bg-zinc-800"
            data-testid="export-team-season-xlsx-btn">
            {exporting === 'xlsx'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            {t('Excel', 'Excel')}
          </Button>
          <Button onClick={handleExportBatch} disabled={!!exporting || loading || !!error}
            variant="outline"
            className="h-9 border-zinc-700 text-zinc-200 hover:bg-zinc-800"
            data-testid="export-team-season-batch-btn"
            title={t('Generates one Athlete Season PDF per athlete, packaged in a ZIP. Large squads can exceed the request timeout.',
                     'Genera un PDF di stagione per atleta, in un ZIP. Squadre grandi possono superare il timeout.')}>
            {exporting === 'batch'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Download className="w-4 h-4 mr-2" />}
            {t('Batch ZIP', 'ZIP Atleti')}
          </Button>
        </div>
      </div>

      {/* Small sample banner */}
      {data.small_sample && (
        <div className="bg-amber-950/30 border border-amber-500/40 rounded p-3 text-xs text-amber-200 flex items-center gap-2"
          data-testid="small-sample-banner">
          <AlertTriangle className="w-4 h-4" />
          <b>{t('Small Sample Size', 'Campione Ridotto')}</b> —
          {' '}{t('Interpret with caution. Squad size below 3 athletes.',
                  'Interpretare con cautela. Squadra inferiore a 3 atleti.')}
        </div>
      )}

      {/* 1 — Hero KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="team-season-kpis">
        <KpiCard label="Squad Availability" value={`${kpis.availability_pct}%`}
          accent={kpis.availability_pct >= 90 ? 'good' : kpis.availability_pct >= 70 ? 'warn' : 'critical'}
          hint={`${availability.totals.available_days} d available`}
          testid="team-kpi-availability" />
        <KpiCard label="Total Team Load" value={`${kpis.total_load.toLocaleString()} AU`}
          hint={`${load.athlete_session_occurrences} ${t('Athlete-session occurrences', 'Occorrenze atleta-sessione')}`}
          testid="team-kpi-load" />
        <KpiCard label="Wellness Index" value={kpis.wellness_index ? `${kpis.wellness_index}/10` : '—'}
          accent={kpis.wellness_index >= 7 ? 'good' : kpis.wellness_index >= 5.5 ? 'warn' : (kpis.wellness_index ? 'critical' : 'primary')}
          hint={`${wellness.entry_count} entries · ${wellness.red_flags} red flags`}
          testid="team-kpi-wellness" />
        <KpiCard label="Injury Burden" value={`${kpis.injury_burden_days} d`}
          accent={kpis.injury_burden_days === 0 ? 'good' : kpis.injury_burden_days < 100 ? 'warn' : 'critical'}
          hint={`${injuries.total_injuries} injuries`}
          testid="team-kpi-injury-burden" />
      </div>

      {/* 2 — Narrative */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="team-season-narrative">
        <SectionTitle icon={Award} title={t('Season Summary', 'Riassunto di Stagione')}
          subtitle={t('Auto-generated · deterministic', 'Generato automaticamente · deterministico')} />
        <p className="text-sm text-zinc-300 leading-relaxed">{narrative}</p>
      </Card>

      {/* 3 — Squad Availability + Trend */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="team-section-availability">
        <SectionTitle icon={Calendar} title={t('Squad Availability Timeline', 'Disponibilità Squadra')}
          subtitle={`${availability.totals.available_days} available · ${availability.totals.rtp_days} RTP · ${availability.totals.lost_days} lost (squad-days)`} />
        <SquadAvailability weeks={availability.weeks} />
        <div className="mt-5 pt-4 border-t border-zinc-800">
          <div className="text-xs uppercase text-zinc-500 mb-2">{t('Weekly Availability Trend (%)', 'Trend Settimanale Disponibilità (%)')}</div>
          <AvailabilityTrend trend={trend} />
        </div>
      </Card>

      {/* 4 — Training Load */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="team-section-load">
        <SectionTitle icon={Activity} title={t('Training Load', 'Carico Allenamento')}
          subtitle={`Total ${kpis.total_load.toLocaleString()} AU · Weekly avg ${load.weekly_avg.toLocaleString()} AU`} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
          <div><span className="text-zinc-500">Total: </span><span className="text-zinc-100 font-semibold">{kpis.total_load.toLocaleString()}</span></div>
          <div><span className="text-zinc-500">Weekly avg: </span><span className="text-zinc-100 font-semibold">{load.weekly_avg.toLocaleString()}</span></div>
          <div><span className="text-zinc-500">Highest: </span><span className="text-zinc-300 font-semibold">{load.highest_week?.load?.toLocaleString() || '—'}</span></div>
          <div><span className="text-zinc-500">Lowest: </span><span className="text-amber-400 font-semibold">{load.lowest_week?.load?.toLocaleString() || '—'}</span></div>
        </div>
        <WeeklyLoadChart weekly={load.weekly} />
      </Card>

      {/* 5 — Wellness */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="team-section-wellness">
        <SectionTitle icon={Heart} title={t('Wellness', 'Benessere')}
          subtitle={`${wellness.entry_count} entries · ${wellness.red_flags} red flags · squad avg ${wellness.avg_overall || '—'}/10`} />
        <WellnessChart weekly={wellness.weekly_average} />
      </Card>

      {/* 6 — Performance Progress */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="team-section-progress">
        <SectionTitle icon={Zap} title={t('Performance Progress', 'Progresso Performance')}
          subtitle={t('Mean individual change · Comparable athletes', 'Variazione individuale media · Atleti confrontabili')} />
        <div className="grid grid-cols-1 gap-6">
          <LongitudinalProgress label="Jump" block={performance.jump} team testId="team-longitudinal-jump" />
          <LongitudinalProgress label="VBT" block={performance.vbt} team testId="team-longitudinal-vbt" />
          <LongitudinalProgress label="Aerobic" block={performance.aerobic} team testId="team-longitudinal-aerobic" />
        </div>
        <LongitudinalSummary performance={performance} team t={t} testId="team-longitudinal-summary" />
      </Card>

      {/* 7 — Training Distribution */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="team-section-distribution">
        <TeamCalendarSummary calendar={data.team_calendar} t={t} testId="team-season-calendar" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DistributionDonut categories={td.categories} />
          <div className="text-xs space-y-2">
            {td.categories.length ? td.categories.filter(c => c.unique_session_count).map((c, i) => (
              <div key={c.category} className="flex items-center justify-between gap-3 border-b border-zinc-800/60 pb-1.5" data-testid={`team-calendar-category-${c.category.toLowerCase().replace(/\s/g, '-')}`}>
                <span className="text-zinc-400 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: c.category === 'RTP' ? '#EF4444' : PALETTE[i % PALETTE.length] }} />
                  {c.category}
                </span>
                <span className="text-zinc-100 tabular-nums">{c.minutes == null ? '—' : c.minutes.toLocaleString()} min · {c.percentage == null ? '—' : `${c.percentage}%`}</span>
              </div>
            )) : <NoData />}
          </div>
        </div>
      </Card>

      {/* 7b — Match Exposure (P0.5.13A) */}
      <TeamMatchExposureSection
        api={api}
        teamId={teamId}
        dateFrom={data.period.start_date}
        dateTo={data.period.end_date}
        t={t}
      />

      {/* 8 — Injury Analytics */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="team-section-injuries">
        <SectionTitle icon={ShieldAlert} title={t('Injury Analytics', 'Analisi Infortuni')}
          subtitle={`${injuries.total_injuries} injuries · ${injuries.days_lost} days lost · ${injuries.active_count} active · ${injuries.rate_per_1000_days}/1000 ath-days`} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="text-[10px] uppercase text-zinc-500 mb-2">{t('By Type', 'Per Tipo')}</div>
            {injuries.by_type?.length ? injuries.by_type.map((t2, i) => (
              <div key={i} className="flex justify-between text-xs py-1 border-b border-zinc-800/60 last:border-0">
                <span className="text-zinc-300">{t2.type}</span>
                <span className="text-zinc-100 font-semibold">{t2.count}</span>
              </div>
            )) : <NoData />}
          </div>
          <div>
            <div className="text-[10px] uppercase text-zinc-500 mb-2">{t('By Body Region', 'Per Area Corporea')}</div>
            {injuries.by_region?.length ? injuries.by_region.map((r, i) => (
              <div key={i} className="flex justify-between text-xs py-1 border-b border-zinc-800/60 last:border-0">
                <span className="text-zinc-300">{r.region}</span>
                <span className="text-zinc-100 font-semibold">{r.count}</span>
              </div>
            )) : <NoData />}
          </div>
          <div>
            <div className="text-[10px] uppercase text-zinc-500 mb-2">{t('Recurrences', 'Recidive')}</div>
            {injuries.recurrences?.length ? injuries.recurrences.map((r, i) => (
              <div key={i} className="flex justify-between text-xs py-1 border-b border-zinc-800/60 last:border-0">
                <span className="text-zinc-300">{r.type}</span>
                <span className="text-amber-400 font-semibold">{r.athletes_affected} ath.</span>
              </div>
            )) : <div className="text-xs text-zinc-500 italic py-2">No recurrences flagged.</div>}
            <div className="text-[10px] uppercase text-zinc-500 mt-4 mb-1">{t('Avg recovery', 'Recupero medio')}</div>
            <div className="text-sm font-semibold text-zinc-100">{injuries.avg_recovery_days} d</div>
          </div>
        </div>
        <div className="mt-5 pt-4 border-t border-zinc-800">
          <div className="text-xs uppercase text-zinc-500 mb-2">{t('Monthly Injury Trend', 'Trend Mensile Infortuni')}</div>
          <MonthlyInjuryTrend monthly={injuries.monthly_trend} />
        </div>
      </Card>

      {/* 9 — Rankings & Outliers */}
      <div data-testid="team-section-rankings">
        <SectionTitle icon={Trophy} title={t('Rankings & Outliers', 'Classifiche e Outlier')}
          subtitle={t('Top 5 · Bottom 5 · Risk flags', 'Top 5 · Bottom 5 · Flag di rischio')} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <RankList title={t('Top 5 — Jump (cm)', 'Top 5 — Salto (cm)')}
            rows={rankings.top_jump} suffix=" cm" testid="rank-top-jump" />
          <RankList title={t('Top 5 — Peak Power (W)', 'Top 5 — Potenza (W)')}
            rows={rankings.top_power} suffix=" W" testid="rank-top-power" />
          <RankList title={t('Top 5 — Availability', 'Top 5 — Disponibilità')}
            rows={rankings.top_availability} suffix="%" testid="rank-top-avail" />
          <RankList title={t('Bottom 5 — Jump', 'Bottom 5 — Salto')}
            rows={rankings.bottom_jump} suffix=" cm" testid="rank-bottom-jump" />
          <RankList title={t('Bottom 5 — Peak Power', 'Bottom 5 — Potenza')}
            rows={rankings.bottom_power} suffix=" W" testid="rank-bottom-power" />
          <RankList title={t('Bottom 5 — Availability', 'Bottom 5 — Disponibilità')}
            rows={rankings.bottom_availability} suffix="%" testid="rank-bottom-avail" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
          <FlagList title={t('Asymmetry Flags', 'Asimmetrie')}
            rows={outliers.asymmetry_flags}
            icon={AlertTriangle} accent="amber" testid="flags-asymmetry"
            render={(r) => (
              <>
                <span className="text-zinc-300">{r.name}</span>
                <span className="text-amber-400 font-semibold">{r.asymmetry_pct}% (L {r.left_cm} / R {r.right_cm})</span>
              </>
            )} />
          <FlagList title={t('RTP Flags', 'In RTP')}
            rows={outliers.rtp_flags}
            icon={ShieldAlert} accent="red" testid="flags-rtp"
            render={(r) => (
              <>
                <span className="text-zinc-300">{r.name}</span>
                <span className="text-red-300">{r.injury_type} · {r.injury_date}</span>
              </>
            )} />
          <FlagList title={t('Low Wellness', 'Benessere Basso')}
            rows={outliers.low_wellness_flags}
            icon={Heart} accent="amber" testid="flags-wellness"
            render={(r) => (
              <>
                <span className="text-zinc-300">{r.name}</span>
                <span className="text-amber-400 font-semibold">{r.avg_overall}/10</span>
              </>
            )} />
          <FlagList title={t('Load Risk', 'Rischio Carico')}
            rows={outliers.load_risk_flags}
            icon={Activity} accent="red" testid="flags-load"
            render={(r) => (
              <>
                <span className="text-zinc-300">{r.name}</span>
                <span className="text-red-300">{r.weekly_avg.toLocaleString()} ({fmtPct(r.vs_squad_mean_pct)} vs mean)</span>
              </>
            )} />
        </div>
      </div>

      {/* 10 — Comparisons */}
      <div data-testid="team-section-comparisons">
        <SectionTitle icon={ChevronRight} title={t('Comparisons', 'Confronti')}
          subtitle={t('vs previous season · other teams · category average',
                     'vs stagione precedente · altre squadre · media categoria')} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <ComparisonCard title={t('vs Previous Season', 'vs Stagione Precedente')}
            mode={comparisons?.previous_season} testid="team-cmp-prev" />
          <ComparisonCard title={t('vs Other Teams', 'vs Altre Squadre')}
            mode={comparisons?.other_teams} testid="team-cmp-other" />
          <ComparisonCard title={t('vs Category Average', 'vs Media Categoria')}
            mode={comparisons?.category_average} testid="team-cmp-category" />
        </div>
      </div>

      {/* 11 — Findings & Recommendations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="team-section-findings">
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <SectionTitle icon={Award} title={t('Key Findings', 'Risultati Chiave')} />
          <ul className="space-y-2 text-sm text-zinc-300">
            {findings.map((f, i) => <li key={i}>• {f}</li>)}
          </ul>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <SectionTitle icon={ChevronRight} title={t('Recommendations', 'Raccomandazioni')} />
          <ul className="space-y-2 text-sm text-zinc-300">
            {recommendations.map((r, i) => <li key={i}>• {r}</li>)}
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default TeamSeasonAnalyticsPage;
